/* eslint-disable sonarjs/cognitive-complexity */
import _ from 'lodash';
import { ChartFragment, ChartFragmentsRS } from 'models/api-response';
import { FragmentType, UserRole } from 'models/enum';
import { InstructorChartSourceQuery, Locales, NAV_ID } from 'constants/app.constant';
import { chartService } from 'services';
import { getInstructorDetails } from 'services/assignment.service';

const groupKey = (f) => `${(f?.navElementId || '').toUpperCase()}::${(f?.fragmentId || '').toUpperCase()}`;
const groupKeyMergeContent = (f) => `${(f?.navElementId || '').toUpperCase()}`;

const groupByDynamic = (arr: any[], mergeSet: Set<string>) =>
  _.groupBy(arr, (f) => {
    const navUpper = (f?.navElementId || '').toUpperCase();
    return mergeSet.has(navUpper) ? groupKeyMergeContent(f) : groupKey(f);
  });

const navIdFromKey = (key: string) => (key.includes('::') ? key.split('::')[0] : key);

const isPresent = (v): boolean => {
  if (_.isNil(v)) return false;
  if (_.isString(v)) return _.trim(v).length > 0;
  if (_.isArray(v)) return v.length > 0;
  if (_.isPlainObject(v)) return Object.keys(v).length > 0;
  return true;
};

function deepMergePreferIncoming<T>(base: T, incoming: T): T {
  const customizer = (objValue, srcValue) => {
    if (_.isArray(objValue) || _.isArray(srcValue)) {
      return isPresent(srcValue) ? srcValue : objValue;
    }
    if (_.isPlainObject(objValue) && _.isPlainObject(srcValue)) {
      return undefined;
    }
    return isPresent(srcValue) ? srcValue : objValue;
  };

  return _.mergeWith({}, base, incoming, customizer);
}

const reduceWithinSource = (frags) => {
  if (!frags?.length) return null;
  const filtered = frags?.length > 1 ? frags.filter((f) => f.fragmentType !== 'STATUS') : frags;
  const sorted = _.orderBy(filtered, (x) => new Date(x?.createdAt ?? 0).getTime(), 'asc');
  return _.reduce(
    sorted,
    (acc, cur) => {
      if (!acc) return cur;
      return {
        ...acc,
        chartData: deepMergePreferIncoming(acc.chartData, cur.chartData),
        fragmentId: cur.fragmentId ?? acc.fragmentId,
        createdAt: cur.createdAt ?? acc.createdAt,
        updatedAt: cur.updatedAt ?? acc.updatedAt,
        modifier: cur.modifier ?? acc.modifier,
        creator: cur.creator ?? acc.creator,
        chartingAt: cur.chartingAt ?? acc.chartingAt,
        completedAt: cur.completedAt ?? acc.completedAt,
        ...cur
      };
    },
    null
  );
};

const dedupeFragments = <T extends { id?: string; fragmentId?: string; navElementId?: string; createdAt?: string; [k: string]: any }>(items: T[]): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  (items || []).forEach((f) => {
    const key = f?.fragmentId || (f?.navElementId ? `${f.fragmentId}-${f?.createdAt || ''}` : JSON.stringify(f));
    if (!seen.has(key)) {
      seen.add(key);
      out.push(f);
    }
  });
  return out;
};

const toEpoch = (ts) => {
  const n = new Date(ts).getTime();
  return Number.isNaN(n) ? -Infinity : n;
};

export const combineStudentInstructor = (student, instructor, mergeNavElementIds: string[]) => {
  const mergeSet = new Set((mergeNavElementIds ?? []).map((s) => _.toUpper(s || '')));

  const groupS = groupByDynamic(student ?? [], mergeSet);
  const groupI = groupByDynamic(instructor ?? [], mergeSet);
  const allKeys = new Set<string>([...Object.keys(groupS), ...Object.keys(groupI)]);

  const mergedResults = [];
  const passthrough = [];

  Array.from(allKeys).forEach((k) => {
    const navIdUpper = navIdFromKey(k);
    const needMergeContent = mergeSet.has(navIdUpper);

    const sCombined = reduceWithinSource(groupS[k] || []);
    const iCombined = reduceWithinSource(groupI[k] || []);

    if (needMergeContent) {
      if (sCombined || iCombined) {
        let chosenData = sCombined ?? iCombined;
        if (sCombined && iCombined) {
          chosenData = toEpoch(sCombined.chartingAt) > toEpoch(iCombined.chartingAt) ? sCombined : iCombined;
        }

        mergedResults.push({
          ...chosenData,
          __source: chosenData === sCombined ? 'student-merged' : 'instructor-merged',
          chartData: chosenData?.chartData
        });
      }
    } else {
      if (sCombined) passthrough.push(sCombined);
      if (iCombined) passthrough.push(iCombined);
    }
  });

  return dedupeFragments([...passthrough, ...mergedResults]);
};

export const isInstructorChartSource = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const hash = window.location.hash || '';
  const query = hash.includes('?') ? hash.substring(hash.indexOf('?') + 1) : window.location.search.substring(1);
  return new URLSearchParams(query).get(InstructorChartSourceQuery.PARAM) === InstructorChartSourceQuery.VALUE;
};

const INSTRUCTOR_SOURCES = new Set(['instructor', 'instructor-merged']);

export const excludeInstructorSetupFragments = <T extends { __source?: string; chartData?: any }>(fragments?: T[]): T[] =>
  (fragments ?? []).filter((fragment) => {
    const source = fragment?.['__source'];
    if (fragment?.chartData?.isDistractor === true) return false;
    if (source && INSTRUCTOR_SOURCES.has(source)) return false;
    return true;
  });

export async function loadFragmentsWithInstructor({
  assessment,
  navIds,
  loadedChartId = null,
  fragmentTypes = [FragmentType.CHARTING, FragmentType.AUTHORED, FragmentType.STATUS],
  loggedInUserId,
  userRole,
  isInstructorSaveData,
  isInstructorSaveDataUS,
  locale,
  dataAssessmentInstructor,
  setDataAssessmentInstructor,
  showAlert
}: any): Promise<ChartFragment[]> {
  const mergeNavElementIds = [
    NAV_ID.PATIENT,
    NAV_ID.HISTORY_PHYSICAL,
    NAV_ID.MEDICAL_HISTORY,
    NAV_ID.PSYCHIATRIC_HISTORY,
    NAV_ID.OBSTETRIC_HISTORY,
    NAV_ID.REVIEW_OF_SYSTEMS,
    NAV_ID.PHYSICAL_EXAMINATION,
    NAV_ID.IMPRESSIONS,
    NAV_ID.MENTAL_STATUS,
    NAV_ID.ADMISSION_DATA,
    NAV_ID.ADMISSION_HISTORY,
    NAV_ID.HEALTH_HISTORY,
    NAV_ID.PLANNING_HOSPITAL_STAY,
    NAV_ID.ALLERGY_INFORMATION
  ];

  const chartId = loadedChartId || assessment.simChartId;
  const isInstructorChartView = isInstructorChartSource();
  const instructorAccessSelfCharting = assessment.studentId === loggedInUserId && userRole === UserRole.INSTRUCTOR;

  const isSaveDataEnabled = locale === Locales.EN_AU ? isInstructorSaveData : isInstructorSaveDataUS;

  const shouldFetchInstructor = (isSaveDataEnabled || isInstructorChartView) && !instructorAccessSelfCharting;
  const studentPromise: Promise<ChartFragmentsRS> = isInstructorChartView
    ? Promise.resolve({ data: [] })
    : chartService.loadFragments({ chartId, navIds, fragmentTypes }).catch((err) => {
        if (showAlert) {
          showAlert(
            'alertUnableLoadStuSaveData',
            'Unable to load Student Chart Data',
            'We couldn’t retrieve the student entries for this chart. Instructor-entered data is still displayed, so you can continue reviewing the chart.'
          );
          return { data: [] };
        }
        throw err;
      });
  const instructorPromise: Promise<ChartFragmentsRS> = shouldFetchInstructor
    ? (async () => {
        const canUseCachedInstructorChartId = userRole === UserRole.INSTRUCTOR;
        let instructorChartId = canUseCachedInstructorChartId ? dataAssessmentInstructor?.simChartId : null;
        if (!instructorChartId) {
          try {
            const { data } = await getInstructorDetails(assessment.eolsAssignmentId, loggedInUserId);
            if (data && setDataAssessmentInstructor) {
              setDataAssessmentInstructor(data);
            }
            instructorChartId = data?.simChartId;
          } catch {
            instructorChartId = null;
          }
        }

        if (!instructorChartId) return { data: [] };
        return chartService
          .loadFragments({
            chartId: instructorChartId,
            navIds,
            fragmentTypes
          })
          .catch((err) => {
            if (showAlert) {
              showAlert(
                'alertUnableLoadInsSaveData',
                'Unable to load Instructor Chart Data',
                'We couldn’t retrieve the instructor’s entries for this chart. Student-entered data is still displayed, so you can continue reviewing the chart.'
              );
              return { data: [] };
            }
            throw err;
          });
      })()
    : Promise.resolve({ data: [] });

  const [studentRes, instructorRes] = await Promise.all([studentPromise, instructorPromise]);

  const student = (studentRes?.data ?? []).map((item) => ({ ...item, __source: 'student' }));
  const instructor = (instructorRes?.data ?? []).map((item) => ({ ...item, __source: 'instructor' }));

  return combineStudentInstructor(student, instructor, mergeNavElementIds);
}
